import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Power, Trash2, Users } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { usePerm } from '@/shared/hooks/usePerm';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import {
  apiStaffDelete,
  apiStaffStatusPatch,
  useStaffList,
} from '@/entities/staff';
import { formatDate } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';
import type { ApiStaffGetItem } from '@/shared/types/api/staff';

const PAGE_SIZE = 20;

const isSuperStaff = (s: ApiStaffGetItem): boolean => {
  if (typeof s.is_super === 'boolean') return s.is_super;
  return s.role === null;
};

export const WidgetsStaffMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const canCreate = usePerm('staff.create');
  const canEdit = usePerm('staff.edit');
  const canDelete = usePerm('staff.delete');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [deleteTarget, setDeleteTarget] = useState<ApiStaffGetItem | null>(null);
  const [toggleTarget, setToggleTarget] = useState<ApiStaffGetItem | null>(null);

  const params = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
    }),
    [debouncedSearch],
  );

  const query = useStaffList(params);
  const all = query.data ?? [];
  const total = all.length;
  const pageStart = (page - 1) * PAGE_SIZE;
  const rows = all.slice(pageStart, pageStart + PAGE_SIZE);

  const toggleStatus = useMutate({
    api: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiStaffStatusPatch(id, { is_active: isActive }),
    invalidateKeys: [queryKeys.staff.all],
    onSuccess: (_d, vars) => {
      toast.success(vars.isActive ? t('staff.toast.activated') : t('staff.toast.deactivated'));
      setToggleTarget(null);
    },
    onError: (_e, m) => toast.error(m),
  });

  const remove = useMutate({
    api: (id: string) => apiStaffDelete(id),
    invalidateKeys: [queryKeys.staff.all],
    onSuccess: () => {
      toast.info(t('staff.toast.deleted'));
      setDeleteTarget(null);
    },
    onError: (_e, m) => toast.error(m),
  });

  const columns = useMemo<ColumnDef<ApiStaffGetItem>[]>(
    () => [
      {
        accessorKey: 'full_name',
        header: t('staff.list.columns.fullName'),
        cell: (info) => <span className="font-medium text-ink">{info.row.original.full_name}</span>,
      },
      {
        accessorKey: 'username',
        header: t('staff.list.columns.username'),
        cell: (info) => <span className="font-mono text-xs">{info.row.original.username}</span>,
      },
      {
        accessorKey: 'phone_number',
        header: t('staff.list.columns.phone'),
        cell: (info) => info.row.original.phone_number ?? '—',
      },
      {
        accessorKey: 'role',
        header: t('staff.list.columns.role'),
        cell: (info) => {
          const row = info.row.original;
          if (isSuperStaff(row)) {
            return (
              <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-800 whitespace-nowrap">
                {t('staff.list.superBadge')}
              </span>
            );
          }
          return row.role?.name ? (
            <span className="text-sm text-ink">{row.role.name}</span>
          ) : (
            <span className="text-ink-muted">—</span>
          );
        },
      },
      {
        accessorKey: 'is_active',
        header: t('staff.list.columns.status'),
        cell: (info) => (
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
              info.row.original.is_active
                ? 'border-emerald-200 bg-emerald-100 text-emerald-800'
                : 'border-slate-200 bg-slate-100 text-slate-700',
            )}
          >
            {info.row.original.is_active ? t('staff.list.active') : t('staff.list.inactive')}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: t('staff.list.columns.createdAt'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('staff.list.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-1.5">
              {canEdit && (
                <IconButton
                  label={t('common.edit')}
                  onClick={() => navigate(`/staff/${row.id}/edit`)}
                >
                  <Pencil className="h-4 w-4" />
                </IconButton>
              )}
              {canEdit && (
                <IconButton
                  label={row.is_active ? t('staff.list.deactivate') : t('staff.list.activate')}
                  onClick={() => setToggleTarget(row)}
                >
                  <Power className={cn('h-4 w-4', row.is_active ? 'text-amber-600' : 'text-emerald-600')} />
                </IconButton>
              )}
              {canDelete && (
                <IconButton label={t('common.delete')} variant="danger" onClick={() => setDeleteTarget(row)}>
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              )}
            </div>
          );
        },
      },
    ],
    [toggleStatus, t, canEdit, canDelete, navigate],
  );

  return (
    <>
      <PageHeader
        title={t('staff.list.title')}
        subtitle={t('staff.list.subtitle')}
        right={
          canCreate ? (
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/staff/new')}
              title={t('staff.list.newStaff')}
              aria-label={t('staff.list.newStaff')}
            >
              <span className="hidden sm:inline">{t('staff.list.newStaff')}</span>
            </Button>
          ) : null
        }
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar
          searchValue={search}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('staff.list.searchPlaceholder')}
        />

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('staff.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <DataTable
              data={rows}
              columns={columns}
              isLoading={query.isLoading}
              emptyTitle={t('staff.list.emptyTitle')}
              emptyIcon={<Users className="h-8 w-8" />}
              getRowId={(row) => row.id}
              numbered
              pageIndex={page - 1}
              pageSize={PAGE_SIZE}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={t('staff.confirmDelete.title')}
        description={t('staff.confirmDelete.description', { name: deleteTarget?.full_name ?? '' })}
        confirmLabel={t('staff.confirmDelete.confirm')}
        confirmVariant="danger"
        loading={remove.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
      />
      <ConfirmDialog
        open={Boolean(toggleTarget)}
        title={
          toggleTarget?.is_active
            ? t('staff.confirmToggle.deactivate.title')
            : t('staff.confirmToggle.activate.title')
        }
        description={
          toggleTarget?.is_active
            ? t('staff.confirmToggle.deactivate.description', { name: toggleTarget?.full_name ?? '' })
            : t('staff.confirmToggle.activate.description', { name: toggleTarget?.full_name ?? '' })
        }
        confirmLabel={
          toggleTarget?.is_active
            ? t('staff.confirmToggle.deactivate.confirm')
            : t('staff.confirmToggle.activate.confirm')
        }
        confirmVariant={toggleTarget?.is_active ? 'danger' : 'primary'}
        loading={toggleStatus.isPending}
        onCancel={() => setToggleTarget(null)}
        onConfirm={() =>
          toggleTarget && toggleStatus.mutate({ id: toggleTarget.id, isActive: !toggleTarget.is_active })
        }
      />
    </>
  );
};
